import { provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import { ApplicationConfig, ErrorHandler, inject, provideAppInitializer, provideZonelessChangeDetection } from '@angular/core';
import { provideRouter, Router, TitleStrategy, withDisabledInitialNavigation, withInMemoryScrolling } from '@angular/router';
import type { EgLoadedBrand } from '@expertgroup/shell/brand';
import { providePrimeNG } from 'primeng/config';
import { ConfirmationService, MessageService } from 'primeng/api';
import { EgSlatePreset } from './app/theme/eg-slate-preset';
import { appRoutes } from './app.routes';
import { authConfig } from './app/core/auth/auth.config';
import { graphqlProvider } from './app/core/graphql/graphql.provider';
import { launcherProviders } from './app/core/launcher/launcher.providers';
import { AuthService } from './app/core/auth/auth.service';
import { GlobalErrorHandler } from './app/core/error/global-error-handler';
import { I18n } from './app/core/i18n/i18n';
import { IdentityTitleStrategy } from './app/core/identity-title.strategy';
import { provideIdentityBrand } from './app/layout/identity-brand';

/**
 * The application's providers. `brand` is the mask of the host this page is served on, loaded from Identity before bootstrap
 * (`loadEgBrand` in main.ts, shell 0.16); `null` is the generic look — no logo, no mark, the product name only.
 */
export const createAppConfig = (brand: EgLoadedBrand | null = null): ApplicationConfig => ({
    providers: [
        provideRouter(appRoutes, withInMemoryScrolling({ anchorScrolling: 'enabled', scrollPositionRestoration: 'enabled' }), withDisabledInitialNavigation()),
        provideHttpClient(withFetch(), withInterceptorsFromDi()),
        provideZonelessChangeDetection(),
        providePrimeNG({
            theme: {
                preset: EgSlatePreset,
                options: {
                    darkModeSelector: '.app-dark',
                    cssLayer: {
                        name: 'primeng',
                        order: 'tailwind-base, primeng, tailwind-utilities'
                    }
                }
            }
        }),
        // Identity's generic default — the product name in the rail and in the tab title (IdentityTitleStrategy), no logo and no
        // mark of its own — under the mask of the host (ADR 0003 of the shell): the rail and the tab take both from the brand service.
        provideIdentityBrand(brand),
        { provide: TitleStrategy, useClass: IdentityTitleStrategy },
        provideAppInitializer(() => void inject(I18n)),
        authConfig,
        graphqlProvider,
        // Launcher: Identity daje pristup trenutnog korisnika i registrovane UI klijente; adrese iz okruzenja su opcioni override.
        launcherProviders,
        MessageService,
        ConfirmationService,
        { provide: ErrorHandler, useClass: GlobalErrorHandler },
        // Process the auth-code callback ONCE, and BEFORE any route guard runs.
        //
        // `autoLoginPartialRoutesGuard` decides auth from a synchronous, storage-only token check
        // and, when it sees no valid tokens, calls the library's `LoginService.login()` directly —
        // a fresh /connect/authorize that writes a NEW nonce. It is not callback-aware and bypasses
        // `AuthService.login()`'s re-entry flag. Under enabled-blocking initial navigation the
        // router's own app-initializer runs that guard CONCURRENTLY with this checkAuth, so the
        // guard overwrites the in-flight callback's nonce before checkAuth validates it ("incorrect
        // nonce" → redirect loop). We therefore DISABLE initial navigation and trigger it manually
        // only after checkAuth has exchanged the code and persisted the tokens, so the guard sees a
        // valid session and never re-initiates login.
        provideAppInitializer(async () => {
            // inject() only works synchronously, BEFORE the first await — capture both here.
            // (Calling inject() after the await throws NG0203: the injection context is gone.)
            const auth = inject(AuthService);
            const router = inject(Router);
            await auth.checkAuth();
            router.initialNavigation();
        })
    ]
});
